import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  FormFeedback,
  Spinner,
  Alert,
} from "reactstrap";
import { uploadImage } from "../../ApiCalls/uploadFiles";
import "./styles/businessFormModal.scss";

const emptyForm = {
  name: "",
  contactEmail: "",
  contactPhone: "",
  contactFirstName: "",
  contactLastName: "",
  website: "",
  logo: "",
  address: "",
  identificationType: "NIT",
  identificationNumber: "",
  countries: "",
  cities: "",
  clientType: "",
};

const toForm = (business) => ({
  name: business.name || "",
  contactEmail: business.contactEmail || "",
  contactPhone: business.contactPhone || "",
  contactFirstName: business.contactFirstName || "",
  contactLastName: business.contactLastName || "",
  website: business.website || "",
  logo: business.logo || "",
  address: business.address || "",
  identificationType: business.identificationType || "NIT",
  identificationNumber: business.identificationNumber || "",
  countries: (business.countries || []).join(", "),
  cities: (business.cities || []).join(", "),
  clientType: business.clientType || "",
});

const splitList = (value) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

function BusinessFormModal({
  isOpen,
  toggle,
  onSubmit,
  isLoading,
  business = null,
}) {
  const [formData, setFormData] = useState(emptyForm);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    setFormData(business ? toForm(business) : emptyForm);
    setPassword("");
    setShowPassword(false);
    setErrors({});
    setUploadError("");
  }, [business, isOpen]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.password;
        return next;
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setUploadError("El logo no puede superar los 5MB");
      e.target.value = "";
      return;
    }

    try {
      setUploadingLogo(true);
      setUploadError("");

      const body = new FormData();
      body.append("image", file);
      const result = await uploadImage(body);

      if (!result?.imageUrl) {
        throw new Error(
          result?.errors?.[0]?.detail || "No se pudo subir el logo"
        );
      }
      setFormData((prev) => ({ ...prev, logo: result.imageUrl }));
    } catch (err) {
      setUploadError(err.message || "No se pudo subir el logo");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "El nombre es requerido";

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = "El email es requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = "Email inválido";
    }

    if (!formData.identificationNumber.trim()) {
      newErrors.identificationNumber =
        "El número de identificación es requerido";
    }

    if (!business && !password.trim()) {
      newErrors.password = "La contraseña de la cuenta principal es requerida";
    } else if (password && password.length < 8) {
      newErrors.password = "La contraseña debe tener al menos 8 caracteres";
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(
      {
        ...formData,
        countries: splitList(formData.countries),
        cities: splitList(formData.cities),
      },
      password
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="business-form-modal">
      <ModalHeader toggle={toggle}>
        <i className="fas fa-building me-2"></i>
        {business ? "Editar empresa" : "Crear nueva empresa"}
      </ModalHeader>
      <ModalBody>
        {uploadError && <Alert color="danger">{uploadError}</Alert>}
        <Form onSubmit={handleSubmit}>
          {/* Logo */}
          <FormGroup>
            <Label>Logo</Label>
            <div className="logo-field">
              <div className="logo-preview">
                {formData.logo ? (
                  <img src={formData.logo} alt="Logo de la empresa" />
                ) : (
                  <i className="fas fa-image" />
                )}
              </div>
              <div className="logo-actions">
                <input
                  type="file"
                  id="business-logo-upload"
                  accept="image/*"
                  onChange={handleLogoChange}
                  disabled={uploadingLogo || isLoading}
                  style={{ display: "none" }}
                />
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={() =>
                    document.getElementById("business-logo-upload").click()
                  }
                  disabled={uploadingLogo || isLoading}
                >
                  {uploadingLogo ? (
                    <>
                      <Spinner size="sm" /> Subiendo...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload me-2"></i>
                      {formData.logo ? "Cambiar logo" : "Subir logo"}
                    </>
                  )}
                </Button>
                {formData.logo && (
                  <Button
                    color="link"
                    size="sm"
                    className="text-danger"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, logo: "" }))
                    }
                    disabled={isLoading}
                  >
                    Quitar
                  </Button>
                )}
              </div>
            </div>
          </FormGroup>

          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="name">Nombre de la empresa *</Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  placeholder="Nombre"
                  value={formData.name}
                  onChange={handleChange}
                  invalid={!!errors.name}
                  disabled={isLoading}
                />
                {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label for="contactEmail">Email de la empresa *</Label>
                <Input
                  type="email"
                  name="contactEmail"
                  id="contactEmail"
                  placeholder="email@empresa.com"
                  value={formData.contactEmail}
                  onChange={handleChange}
                  invalid={!!errors.contactEmail}
                  disabled={isLoading || !!business}
                />
                {errors.contactEmail && (
                  <FormFeedback>{errors.contactEmail}</FormFeedback>
                )}
                {business && (
                  <small className="text-muted">
                    El email es la cuenta de acceso principal y no se puede
                    cambiar desde aquí.
                  </small>
                )}
              </FormGroup>
            </div>
          </div>

          {/* Main account password */}
          <FormGroup>
            <Label for="mainUserPassword">
              {business
                ? "Cambiar contraseña de la cuenta principal"
                : "Contraseña de la cuenta principal *"}
            </Label>
            <InputGroup>
              <Input
                type={showPassword ? "text" : "password"}
                name="mainUserPassword"
                id="mainUserPassword"
                placeholder={
                  business
                    ? "Déjalo en blanco para mantener la actual"
                    : "Mínimo 8 caracteres"
                }
                value={password}
                onChange={handlePasswordChange}
                invalid={!!errors.password}
                disabled={isLoading}
                autoComplete="new-password"
              />
              <Button
                type="button"
                color="secondary"
                outline
                onClick={() => setShowPassword((prev) => !prev)}
                disabled={isLoading}
                title={
                  showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                }
              >
                <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
              </Button>
              {errors.password && <FormFeedback>{errors.password}</FormFeedback>}
            </InputGroup>
            {!errors.password && (
              <small className="text-muted">
                {business
                  ? `Restablece la contraseña de acceso de ${
                      formData.contactEmail || "la cuenta principal"
                    }.`
                  : "Con este correo y contraseña la empresa inicia sesión por primera vez."}
              </small>
            )}
          </FormGroup>

          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="website">Sitio web</Label>
                <Input
                  type="text"
                  name="website"
                  id="website"
                  placeholder="https://www.ejemplo.com"
                  value={formData.website}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label for="address">Dirección</Label>
                <Input
                  type="text"
                  name="address"
                  id="address"
                  placeholder="Calle 123 #456"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </FormGroup>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="identificationType">Tipo de identificación</Label>
                <Input
                  type="select"
                  name="identificationType"
                  id="identificationType"
                  value={formData.identificationType}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  <option value="NIT">NIT</option>
                  <option value="CC">Cédula</option>
                  <option value="CE">Cédula de extranjería</option>
                  <option value="PASSPORT">Pasaporte</option>
                </Input>
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label for="identificationNumber">
                  Número de identificación *
                </Label>
                <Input
                  type="text"
                  name="identificationNumber"
                  id="identificationNumber"
                  placeholder="1234567890"
                  value={formData.identificationNumber}
                  onChange={handleChange}
                  invalid={!!errors.identificationNumber}
                  disabled={isLoading}
                />
                {errors.identificationNumber && (
                  <FormFeedback>{errors.identificationNumber}</FormFeedback>
                )}
              </FormGroup>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="clientType">Tipo de cliente</Label>
                <Input
                  type="select"
                  name="clientType"
                  id="clientType"
                  value={formData.clientType}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  <option value="">Seleccione...</option>
                  <option value="CONCILIACION">CONCILIACION</option>
                  <option value="PQRS">PQRS</option>
                  <option value="DESEMBOLSOS">DESEMBOLSOS</option>
                  <option value="ROLDAN">ROLDAN</option>
                  <option value="LOGISTICS_DEMO">LOGISTICS_DEMO</option>
                  <option value="FORM_P2P">FORM_P2P</option>
                </Input>
              </FormGroup>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="countries">Países</Label>
                <Input
                  type="text"
                  name="countries"
                  id="countries"
                  placeholder="Colombia, México"
                  value={formData.countries}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <small className="text-muted">Separados por coma</small>
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label for="cities">Ciudades</Label>
                <Input
                  type="text"
                  name="cities"
                  id="cities"
                  placeholder="Bogotá, Medellín"
                  value={formData.cities}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <small className="text-muted">Separadas por coma</small>
              </FormGroup>
            </div>
          </div>

          <hr />
          <h6 className="contact-section-title">Información de contacto</h6>
          <div className="row">
            <div className="col-md-6">
              <FormGroup>
                <Label for="contactFirstName">Nombre del contacto</Label>
                <Input
                  type="text"
                  name="contactFirstName"
                  id="contactFirstName"
                  placeholder="Nombre"
                  value={formData.contactFirstName}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup>
                <Label for="contactLastName">Apellido del contacto</Label>
                <Input
                  type="text"
                  name="contactLastName"
                  id="contactLastName"
                  placeholder="Apellido"
                  value={formData.contactLastName}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </FormGroup>
            </div>
          </div>
          <FormGroup>
            <Label for="contactPhone">Teléfono del contacto</Label>
            <Input
              type="text"
              name="contactPhone"
              id="contactPhone"
              placeholder="+57 1 234 5678"
              value={formData.contactPhone}
              onChange={handleChange}
              disabled={isLoading}
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isLoading || uploadingLogo}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Guardando...
            </>
          ) : (
            <>
              <i className="fas fa-save me-2"></i>
              {business ? "Actualizar" : "Crear"}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default BusinessFormModal;
